/**
 * Server-side search for the multi-tag picker (ForgeTagsSelector).
 *
 * A facility group can hold 100k+ tags, so the picker never loads them all: it asks
 * `GET api/baseTags/typeHead` for one page of 50 at a time (on first open, per search, and on scroll)
 * and resolves only the names of the tags already linked. Kept pure so the paging and merge rules are
 * testable on their own.
 */
export const TAG_PICKER_PAGE_SIZE = 50;

/**
 * Query for `GET api/baseTags/typeHead`. The search is lower-cased because the endpoint compares
 * against the lower-cased name; `sortBy` keeps the list alphabetical; `includeTotal: false` skips the
 * server-side COUNT, which cost as much as the page itself (the picker pages on "a full page means
 * there may be more", see hasMorePages).
 */
export function buildTagPickerQuery({ assetId, search = '', page = 1 } = {}) {
  return {
    assetId,
    search: String(search || '').trim().toLowerCase(),
    page: Math.max(1, Number(page) || 1),
    itemsPerPage: TAG_PICKER_PAGE_SIZE,
    sortBy: 'Name',
    includeTotal: false,
  };
}

/** The picker's shape for one tag: { id, name, discriminator }; null for anything without an id. */
export function toPickerTag(tag) {
  if (!tag || !tag.id) return null;
  return {
    id: String(tag.id),
    name: tag.name ? String(tag.name) : String(tag.id),
    discriminator: tag.discriminator || null,
  };
}

/** `existing` followed by the tags of `incoming` whose id is not already there. */
export function appendUnique(existing, incoming) {
  const seen = new Set((existing || []).map((t) => t.id));
  const added = [];
  for (const raw of incoming || []) {
    const tag = toPickerTag(raw);
    if (tag && !seen.has(tag.id)) {
      seen.add(tag.id);
      added.push(tag);
    }
  }
  return [...(existing || []), ...added];
}

/** Ids of `ids` with no entry in `resolvedById` yet. */
export function missingIds(ids, resolvedById) {
  return [...new Set((ids || []).map(String))].filter((id) => !(resolvedById && resolvedById[id]));
}

/**
 * The items the autocomplete shows: the selected tags first (so their chips can render; Vuetify drops a
 * chip whose value is not in `items`), in selection order, then the search results that are not selected.
 * A selected id not resolved yet gets a placeholder so it is never dropped from the value.
 */
export function mergePickerItems(selectedIds, resolvedById, results, placeholderName = '…') {
  const selected = [...new Set((selectedIds || []).map(String))].map(
    (id) => (resolvedById && resolvedById[id]) || { id, name: placeholderName, discriminator: null },
  );
  const selectedSet = new Set(selected.map((t) => t.id));
  return [...selected, ...(results || []).filter((t) => !selectedSet.has(t.id))];
}

/**
 * Whether another page may exist. With a known total (>= 0; older APIs always send one) it is
 * `loaded < total`; without one (total -1 when the count was skipped) a full last page means there may
 * be more.
 */
export function hasMorePages(loadedCount, total, lastPageSize = 0, pageSize = TAG_PICKER_PAGE_SIZE) {
  const known = Number(total);
  if (Number.isFinite(known) && known >= 0) return Number(loadedCount) < known;
  return Number(lastPageSize) >= pageSize;
}
